<template>
  <Transition name="slide-fade">
    <template v-if="qualificationFunction('showLoginedPanel')">
      <div v-if="showPanel.isLeftPanel" class="logined_panel">
        <div class="logined_panel_inner" :class="{'opacity': disabledQualification()}">
          <img
            class="logo" src="@/assets/images/ContecLogo.png"
            alt="Contec_Logo"
          >
          <div class="logined_panel_fixation_area">
            <img
              class="side_logo"
              :src="getLevelImageSrc(accessedMemberInfo.level,'SideLogo')"
              alt="side logo"
            >
            <img
              class="character"
              :src="getLevelImageSrc(accessedMemberInfo.level,'')"
              :alt="`${accessedMemberInfo.level}`"
            >
            <h2 class="character_name" :class="getLevelName()">
              {{ getLevelName() }}
            </h2>
            <h3 class="setellite_name">
              OREUM SAT
            </h3>
          </div>
          <div class="logined_panel_contents_area">
            <TransitionGroup
              name="slide" tag="div"
              class="contents"
            >
              <AccountInfo v-if="qualificationFunction('showAccountInfo')" />
              <div
                v-if="!showPanel.isAccountInfo"
                :class="getLevelName()"
              >
                <div class="location">
                  <h4>
                    <span>
                      LAT
                    </span> <span>
                      {{ getCoordinate("Lat") }}
                    </span>N
                  </h4>
                  <h4>
                    <span>
                      LON
                    </span> <span>
                      {{ getCoordinate("Lon") }}
                    </span>E
                  </h4>
                </div>
                <DownwardsArrowSvg class="downwards_arrow" />
                <h4 class="country_name">
                  ON A MISSION<br /><span>
                    {{ getCountryName }}
                  </span>
                </h4>
              </div>
              <EditPassword v-if="qualificationFunction('showEditPassword')" />
            </TransitionGroup>
            <IconButtons />
          </div>
        </div>
      </div>
    </template>
  </Transition>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';

import AccountInfo from '@/components/common/AccountInfo.vue';
import EditPassword from '@/components/common/EditPassword.vue';
import IconButtons from '@/components/desktop/IconButtons.vue';
import DownwardsArrowSvg from '@/assets/images/Svgs/DownwardsArrowSvg.vue';
import useCommonStore from '@/store/storeCommon';
import useMapStore from '@/store/storeMap';
import useMemberStore from '@/store/storeMember';

const { isImageMode } = storeToRefs(useCommonStore());
const { disabledQualification, showPanel } = useCommonStore();
const { accessedMemberInfo, getLevelImageSrc, getLevelName } = useMemberStore();
const { getCoordinate, satelliteInfo } = useMapStore();

/**
 * 실시간 지역 이름을 가져옵니다.
 */
const getCountryName = computed((): string => {
  return satelliteInfo.locationName === ''
    ? '찾는중'
    : `In ${satelliteInfo.locationName}`;
});

onMounted(() => {
  showPanel.isAccountInfo = true;
});

/**
 * 조건별 함수
 */
const qualificationFunction = (qualification: string): boolean => {
  switch (qualification) {
    case 'showLoginedPanel':
      return !!(accessedMemberInfo.name && !isImageMode.value);
    case 'showAccountInfo':
      return showPanel.isAccountInfo && !showPanel.isEditPassword;
    case 'showEditPassword':
      return showPanel.isAccountInfo && showPanel.isEditPassword;
    default:
      return false;
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/css/_colors.scss';

.logined_panel {
  min-width: 450px;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 101;

  &_inner.opacity {
    opacity: 0.3;
  }

  .slide-enter-active {
    transition: all 0.3s ease-out;
    transition-delay: 0.3s;
  }

  .slide-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
  }

  .logo {
    width: 80px;
    position: absolute;
    top: 31px;
    left: 50%;
    transform: translateX(-50%);
  }

  &_fixation_area {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;

    .side_logo {
      width: 256px;
      margin-bottom: 29.3px;
    }

    .character {
      width: 190.8px;
      margin-bottom: 17.1px;
    }

    .character_name {
      font-family: "Montserrat black 900", sans-serif;
      color: $color-black;
      padding: 1.9px 12.7px;
      background: $color-contec;
      border-radius: 50px;
      margin-bottom: 7px;

      &.DEBRA {
        background: $color-debra;
      }

      &.METE {
        background: $color-mete;
      }

      &.KORONG {
        background: $color-korong;
      }

    }

    .setellite_name {
      font-family: "Poppins ExtraBold 900", sans-serif;
      color: $color-white;
      font-size: 25px;
    }
  }

  &_contents_area {
    height: 350px;
    padding: 15px 0;

    .contents {
      .location {
        color: $color-white;
        margin-top: 50px;

        h4 {
          line-height: 1.2;
          font-size: 17px;
          font-family: "Poppins Medium 500", sans-serif;
          width: 100px;
          display: flex;
          margin: 0 auto;
          gap: 5px;
          text-align: right;

          span:first-child {
            display: inline-block;
            color: $color-mete;
            min-width: 33px;
            text-align: left;
          }

          span:nth-child(2) {
            min-width: 65px;
          }
        }
      }

      .downwards_arrow {
        fill: $color-mete;
        width: 100%;
        margin: 15.9px 0 13.2px;
        display: flex;
        justify-content: center;
      }

      .country_name {
        color: $color-mete;
        text-align: center;
        line-height: 1.3;
        font-size: 20px;

        span {
          font-family: "Poppins ExtraBold 900", sans-serif;
          font-size: 27px;
          text-transform: uppercase;
        }
      }

      .DEBRA {
        .location {
          h4 {
            span:first-child {
              color: $color-debra;
            }
          }
        }

        .downwards_arrow {
          fill: $color-debra;
        }

        .country_name {
          color: $color-debra;
        }
      }

      .METE {
        .location {
          h4 {
            span:first-child {
              color: $color-mete;
            }
          }
        }

        .downwards_arrow {
          fill: $color-mete;
        }

        .country_name {
          color: $color-mete;
        }
      }

      .KORONG {
        .location {
          h4 {
            span:first-child {
              color: $color-korong;
            }
          }
        }

        .downwards_arrow {
          fill: $color-korong;
        }

        .country_name {
          color: $color-korong;
        }
      }

      &.slide-enter-active {
        transition: all 0.3s ease-out;
        transition-delay: 0s;
      }

      &.slide-leave-active {
        transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
      }

      &.slide-enter-from,
      &.slide-leave-to {
        transform: translateX(-100%);
        opacity: 0;
      }
    }
  }
}

@media (min-width: 1921px) {
  .logined_panel {
    justify-content: start;

    .logined_panel_fixation_area {
      margin-top: 144px;
    }

    &_contents_area {
      height: auto;
      padding-top: 20px;
    }
  }

}
</style>